"use client"

import { useEffect, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import Image from "next/image"
import { Calendar, User, Search, ArrowRight } from "lucide-react"
import { supabase } from "@/lib/supabase"
import Link from "next/link"

export interface BlogPost {
  id: string
  title: string
  meta_description: string
  keywords: string
  thumbnail: string
  category: string
  created_at: string
  updated_at: string
  author?: string
  read_time?: string
}

const categories = ["Tất cả", "Tin tức", "Công nghệ", "Sản phẩm", "Môi trường", "Tips & Tricks", "Khác"]

function FeaturedPostSkeleton() {
  return (
    <div className="grid md:grid-cols-2 gap-0 animate-pulse">
      <div className="bg-gray-300 h-64 md:h-auto w-full rounded-md" />
      <div className="p-8 space-y-4">
        <div className="h-6 w-24 bg-gray-300 rounded" />
        <div className="h-10 w-3/4 bg-gray-300 rounded" />
        <div className="h-20 bg-gray-300 rounded" />
        <div className="flex justify-between items-center mt-8">
          <div className="flex space-x-4">
            <div className="h-4 w-12 bg-gray-300 rounded" />
            <div className="h-4 w-16 bg-gray-300 rounded" />
            <div className="h-4 w-14 bg-gray-300 rounded" />
          </div>
          <div className="h-10 w-28 bg-gray-300 rounded" />
        </div>
      </div>
    </div>
  )
}

function PostCardSkeleton() {
  return (
    <Card className="overflow-hidden animate-pulse">
      <div className="bg-gray-300 h-48 w-full rounded-md" />
      <CardHeader className="p-4 space-y-3">
        <div className="h-6 w-3/4 bg-gray-300 rounded" />
        <div className="h-16 w-full bg-gray-300 rounded" />
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <div className="flex justify-between items-center mb-4">
          <div className="h-4 w-12 bg-gray-300 rounded" />
          <div className="h-4 w-16 bg-gray-300 rounded" />
          <div className="h-4 w-14 bg-gray-300 rounded" />
        </div>
        <div className="h-10 w-full bg-gray-300 rounded" />
      </CardContent>
    </Card>
  )
}

export default function BlogSection() {
  const [selectedCategory, setSelectedCategory] = useState("Tất cả")
  const [searchTerm, setSearchTerm] = useState("")
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true)
      try {
        const { data, error } = await supabase
          .from("posts_public")
          .select("*")
          .order("created_at", { ascending: false })

        if (error) throw new Error(error.message)

        const formattedData: BlogPost[] = data.map((item: any) => ({
          id: item.id,
          title: item.title,
          meta_description: item.meta_description || "",
          keywords: item.keywords || "",
          thumbnail: item.thumbnail || "",
          category: item.category || categories[0],
          created_at: item.created_at,
          updated_at: item.updated_at,
          author: item.author || "Admin",
          read_time: item.read_time || "5 phút đọc",
        }))

        setPosts(formattedData)
      } catch (err) {
        console.error("Lỗi khi tải bài viết:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchPosts()
  }, [])

  const filteredPosts = posts.filter((post) => {
    const matchesCategory = selectedCategory === "Tất cả" || post.category === selectedCategory
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.meta_description.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  if (loading)
    return (
      <div className="min-h-screen py-16 px-4 flex flex-col items-center justify-center">
        <Card className="mb-12 w-full max-w-5xl overflow-hidden">
          <FeaturedPostSkeleton />
        </Card>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 w-full max-w-6xl">
          {Array(6)
            .fill(null)
            .map((_, i) => (
              <PostCardSkeleton key={i} />
            ))}
        </div>
      </div>
    )

  return (
    <div className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-4">Blog & Tin Tức</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Cập nhật những tin tức mới nhất về ngành Sợi PP, công nghệ sản xuất và các kiến thức hữu ích
          </p>
        </div>

        {/* Search & Filter */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Tìm kiếm bài viết..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                disabled={loading}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? "bg-primary hover:bg-primary/90" : ""}
                disabled={loading}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        <>
          {/* Featured Post */}
          {filteredPosts.length > 0 && (
            <Card className="mb-12 overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto">
                  <Image
                    src={filteredPosts[0].thumbnail || "/placeholder.svg"}
                    alt={filteredPosts[0].title}
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="p-8">
                  <Badge className="mb-4 bg-[#273647] text-white">{filteredPosts[0].category}</Badge>
                  <h2 className="text-2xl font-bold text-secondary mb-4">{filteredPosts[0].title}</h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">{filteredPosts[0].meta_description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <User className="h-4 w-4" />
                        <span>{filteredPosts[0].author}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(filteredPosts[0].created_at).toLocaleDateString("vi-VN")}</span>
                      </div>
                      <span>{filteredPosts[0].read_time}</span>
                    </div>
                    <Link
                      href={`/blog/${filteredPosts[0].id}`}
                      className="inline-flex items-center bg-[#273647] text-white px-4 py-2 rounded-md transition-colors duration-200"
                    >
                      Đọc tiếp
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Post Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.slice(1).map((post) => (
              <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="relative h-48">
                  <Image src={post.thumbnail || "/placeholder.svg"} alt={post.title} fill className="object-cover" />
                  <Badge className="absolute top-4 left-4 bg-[#273647] text-white">{post.category}</Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-secondary line-clamp-2">{post.title}</CardTitle>
                  <CardDescription className="line-clamp-3">{post.meta_description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-1">
                      <User className="h-4 w-4" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(post.created_at).toLocaleDateString("vi-VN")}</span>
                    </div>
                    <span>{post.read_time}</span>
                  </div>
                  <Link
                    href={`/blog/${post.id}`}
                    className="block w-full text-center border bg-[#273647] text-white px-4 py-2 rounded-md transition-colors duration-200"
                  >
                    Đọc tiếp
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Không tìm thấy bài viết nào phù hợp.</p>
            </div>
          )}
        </>

      </div>
    </div>
  )
}
